import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import {
  Mail,
  Lock,
  LogIn,
  UserPlus,
  Chrome,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import logger from "./utils/logger";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULE_HINT,
  checkNewPassword,
} from "./security/passwordRules";
import {
  signInErrorMessage,
  signUpErrorMessage,
} from "./security/authMessages";

function Login({ onClose }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // `loading` tracks email/password and account-management actions only.
  // Google sign-in has its own `googleLoading` so the two flows don't
  // visually freeze each other's buttons.
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    signup,
    login,
    loginWithGoogle,
    currentUser,
    isPro,
    resetPassword,
    resendEmailVerification,
    isEmailVerified,
    refreshUser,
    getVerificationBlockingState,
    authError,
    persistenceReady,
    logout,
  } = useAuth();

  // Show auth errors from context (e.g., redirect errors)
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  async function handleResendVerification() {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const result = await resendEmailVerification();
      setSuccess(result.message);
    } catch (error) {
      setError(
        error.message ||
          "Failed to resend verification email. Please try again."
      );
    }
    setLoading(false);
  }

  async function handleRefreshUser() {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const result = await refreshUser();
      setSuccess(result.message);
    } catch (error) {
      setError(
        error.message || "Failed to refresh user data. Please try again."
      );
    }
    setLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation for sign up
    if (isSignUp) {
      const passwordProblem = checkNewPassword(password, { email });
      if (passwordProblem) {
        setError(passwordProblem);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
    }

    setLoading(true);

    try {
      if (isSignUp) {
        await signup(email, password);
        setSuccess(
          "Account created successfully! Verification email is being sent... IMPORTANT: You must verify your email before accessing the app. Check your inbox (and spam folder) for the verification email."
        );
        setIsSignUp(false); // Switch to sign in mode
        setPassword("");
        setConfirmPassword("");
      } else {
        const result = await login(email, password);
        if (result?.user && !result.user.emailVerified) {
          setSuccess(
            "Signed in. Please verify your email address before using the planner."
          );
        } else {
          setSuccess("Signed in successfully!");
        }
        if (onClose && result?.user?.emailVerified !== false) {
          setTimeout(onClose, 1500);
        }
      }
    } catch (error) {
      logger.error("Auth error:", error);
      // One message for a wrong email or a wrong password, so the form never
      // reveals which emails have accounts (see security/authMessages.js).
      setError(
        isSignUp
          ? signUpErrorMessage(error.code)
          : signInErrorMessage(error.code)
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError("");
    setSuccess("");

    // Check if auth persistence is ready
    if (!persistenceReady) {
      setError(
        "Authentication system is still initializing. Please wait a moment and try again."
      );
      return;
    }

    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle();

      if (result) {
        setSuccess("Signed in with Google successfully!");
        if (onClose) {
          setTimeout(onClose, 1500);
        }
      }
    } catch (error) {
      logger.error("Google sign-in error:", error);
      setError(error.message || "Google sign-in failed. Please try again.");
    } finally {
      // Always clear googleLoading so a cancelled / timed-out / errored Google
      // popup never leaves the button stuck in "Signing in..." state.
      setGoogleLoading(false);
    }
  }

  async function handlePasswordReset(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const result = await resetPassword(resetEmail);
      setSuccess(result.message);
      setTimeout(() => {
        setShowResetPassword(false);
        setResetEmail("");
      }, 3000);
    } catch (error) {
      setError(
        error.message || "Failed to send reset email. Please try again."
      );
    }
    setLoading(false);
  }

  // If user is already logged in
  if (currentUser && !showResetPassword) {
    // Check if user is blocked due to email verification
    const blockingState = getVerificationBlockingState();

    if (blockingState.blocked) {
      logger.log("🚫 Login: User blocked due to verification:", blockingState);
      logger.log("🔍 Login: Blocking screen render details:", {
        currentUser: currentUser
          ? {
              uid: currentUser.uid,
              email: currentUser.email,
              emailVerified: currentUser.emailVerified,
            }
          : null,
        timestamp: new Date().toISOString(),
      });
      return (
        <div className="p-6">
          <h2
            id="login-dialog-title"
            className="text-lg font-bold mb-4 text-white"
          >
            Email Verification Required
          </h2>

          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 mb-2">
              ⚠️ <strong>Access Blocked:</strong> {blockingState.message}
            </p>
            <p className="text-xs text-red-700">
              For security reasons, you must verify your email address before
              accessing the application.
            </p>
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 mb-2">
              📧 <strong>Verification Email Sent:</strong> We've sent a
              verification email to:
            </p>
            <p className="text-sm font-mono bg-blue-100 p-2 rounded">
              {currentUser?.email || "Email not available"}
            </p>
            <p className="text-xs text-blue-700 mt-2">
              Please check your inbox (and spam folder) and click the
              verification link.
              <strong>Note:</strong> The verification email may take up to 15
              minutes to arrive.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="w-full bg-yellow-600 text-white py-3 px-4 rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors font-medium min-h-[44px]"
            >
              {loading ? "Sending..." : "📧 Resend Verification Email"}
            </button>

            <button
              onClick={handleRefreshUser}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium min-h-[44px]"
            >
              {loading ? "Refreshing..." : "🔄 Check Verification Status"}
            </button>

            <button
              onClick={async () => {
                try {
                  await logout();
                  setError("");
                  setSuccess("");
                } catch (error) {
                  setError("Failed to sign out. Please try again.");
                }
              }}
              className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors min-h-[44px]"
            >
              🔓 Sign Out & Try Different Account
            </button>
          </div>

          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Having trouble?</strong> Check your spam/junk folder
              thoroughly. If using Gmail or Yahoo, emails are often heavily
              filtered. You can use the "Resend Verification Email" button if
              needed. Contact support at aiebookplanner@gmail.com if no email
              arrives after 15 minutes.
            </p>
          </div>
        </div>
      );
    }

    // User is verified - show normal account status
    return (
      <div className="p-6">
        <h2
          id="login-dialog-title"
          className="text-lg font-bold mb-4 text-white"
        >
          Account Status
        </h2>
        <p className="mb-2 text-gray-100">
          <Mail className="inline mr-2" size={16} />
          {currentUser.email}
        </p>
        <p className="mb-2 text-gray-100">
          Email Verification:{" "}
          {isEmailVerified() ? "✅ Verified" : "❌ Not Verified"}
        </p>
        <p className="mb-4 text-gray-100">
          Status: {isPro ? "✅ PRO User" : "⭐ Free User"}
        </p>

        {/* Email verification actions */}
        {!isEmailVerified() && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 mb-2">
              ⚠️ Please verify your email address to access all features.
            </p>
            <div className="space-y-2">
              <button
                onClick={handleResendVerification}
                disabled={loading}
                className="w-full bg-yellow-600 text-white py-2 px-3 rounded text-sm hover:bg-yellow-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Sending..." : "📧 Resend Verification Email"}
              </button>
              <button
                onClick={handleRefreshUser}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-3 rounded text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Refreshing..." : "🔄 Refresh Status"}
              </button>
            </div>
          </div>
        )}

        {onClose && (
          <button
            onClick={onClose}
            className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  // Password reset form
  if (showResetPassword) {
    return (
      <div className="p-6">
        <h2
          id="login-dialog-title"
          className="text-lg font-bold mb-4 text-white"
        >
          Reset Password
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>

        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-200">
              <Mail className="inline mr-1" size={16} />
              Email Address
            </label>
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="w-full p-3 sm:p-2 rounded-xl border border-white/15 bg-white/5 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-base min-h-[44px] backdrop-blur-sm transition-all"
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm flex items-center">
              <AlertCircle className="mr-1" size={16} />
              {error}
            </div>
          )}

          {success && (
            <div className="text-green-400 text-sm flex items-center">
              <CheckCircle className="mr-1" size={16} />
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !resetEmail}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 sm:py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all min-h-[44px]"
          >
            {loading ? "Sending..." : "Send Reset Email"}
          </button>
        </form>

        <button
          onClick={() => {
            setShowResetPassword(false);
            setError("");
            setSuccess("");
          }}
          className="w-full mt-4 text-gray-400 hover:text-white hover:underline"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  // Main sign in/sign up form
  return (
    <div className="p-5 sm:p-6">
      <h2 id="login-dialog-title" className="text-lg font-bold mb-4 text-white">
        {isSignUp ? "Create Your Account" : "Sign In to Your Account"}
      </h2>

      {/* Email verification notice for signup */}
      {isSignUp && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-800 font-medium mb-2">
            ⚠️ <strong>Important:</strong> Email Verification Required
          </p>
          <p className="text-xs text-orange-700">
            After creating your account, you'll receive a verification email.
            You <strong>cannot access the app</strong> until you verify your
            email address. Check your inbox and spam folder.
          </p>
        </div>
      )}

      {/* Google Sign In Button - Prominent placement */}
      <div className="mb-4">
        <button
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading || !persistenceReady}
          className={`w-full border-2 py-3 sm:py-2.5 rounded-lg transition-all flex items-center justify-center font-medium min-h-[44px] ${
            !persistenceReady
              ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
          } ${googleLoading || loading ? "opacity-50" : ""}`}
        >
          <Chrome className="mr-2" size={20} />
          {!persistenceReady
            ? "Initializing..."
            : googleLoading
              ? "Signing in..."
              : "Continue with Google"}
        </button>

        {/* Show persistence status */}
        {!persistenceReady && (
          <p className="text-xs text-gray-400 mt-2 text-center">
            🔄 Setting up secure authentication...
          </p>
        )}
      </div>

      <div className="relative mb-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/15"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-[#13101e] text-gray-400">
            Or continue with email
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-200">
            <Mail className="inline mr-1" size={16} />
            Email Address
          </label>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 sm:p-2 rounded-xl border border-white/15 bg-white/5 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-base min-h-[44px] backdrop-blur-sm transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-200">
            <Lock className="inline mr-1" size={16} />
            Password
          </label>
          <input
            type="password"
            required
            autoComplete={isSignUp ? "new-password" : "current-password"}
            minLength={isSignUp ? PASSWORD_MIN_LENGTH : undefined}
            maxLength={isSignUp ? PASSWORD_MAX_LENGTH : undefined}
            placeholder={
              isSignUp
                ? `Create a password (min ${PASSWORD_MIN_LENGTH} characters)`
                : "Enter your password"
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-3 sm:p-2 rounded-xl border border-white/15 bg-white/5 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-base min-h-[44px] backdrop-blur-sm transition-all"
          />
          {isSignUp && (
            <p className="mt-1 text-xs text-gray-400">{PASSWORD_RULE_HINT}</p>
          )}
        </div>

        {isSignUp && (
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-200">
              <Lock className="inline mr-1" size={16} />
              Confirm Password
            </label>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              maxLength={PASSWORD_MAX_LENGTH}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 sm:p-2 rounded-xl border border-white/15 bg-white/5 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none text-base min-h-[44px] backdrop-blur-sm transition-all"
            />
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm flex items-start">
            <AlertCircle className="mr-1 mt-0.5 flex-shrink-0" size={16} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="text-green-400 text-sm flex items-center">
            <CheckCircle className="mr-1" size={16} />
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || googleLoading}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 sm:py-2.5 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all font-medium flex items-center justify-center min-h-[44px]"
        >
          {loading ? (
            "Processing..."
          ) : isSignUp ? (
            <>
              <UserPlus className="mr-2" size={18} />
              Create Account
            </>
          ) : (
            <>
              <LogIn className="mr-2" size={18} />
              Sign In
            </>
          )}
        </button>
      </form>

      <div className="mt-4 space-y-2">
        {!isSignUp && (
          <button
            onClick={() => {
              setShowResetPassword(true);
              setError("");
              setSuccess("");
            }}
            className="w-full text-blue-400 hover:text-blue-300 hover:underline text-sm"
          >
            Forgot your password?
          </button>
        )}

        <button
          onClick={() => {
            setIsSignUp(!isSignUp);
            setError("");
            setSuccess("");
            setPassword("");
            setConfirmPassword("");
          }}
          className="w-full text-gray-300 hover:text-white text-sm"
        >
          {isSignUp
            ? "Already have an account? Sign In"
            : "Don't have an account? Create one"}
        </button>
      </div>

      {onClose && (
        <button
          onClick={onClose}
          className="w-full mt-4 text-gray-400 hover:text-white text-sm"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

export default Login;
